import React from 'react'
import { Box, Typography } from '@mui/material'
import ConfirmDialog from './shared/dialogs/ConfirmDialog'

interface DisconnectDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  fpCode: string
  franchiseName: string
  systemDisplayName: string
  tenantName?: string
  loading?: boolean
}

/**
 * Confirm disconnecting a franchise from an external system. Now a thin
 * wrapper over the shared `ConfirmDialog` (danger tone + acknowledgement);
 * the prop contract is unchanged so existing call sites
 * (SystemConnections, ManageConnections) keep working as-is.
 */
const DisconnectDialog: React.FC<DisconnectDialogProps> = ({
  open,
  onClose,
  onConfirm,
  fpCode,
  franchiseName,
  systemDisplayName,
  tenantName,
  loading = false,
}) => (
  <ConfirmDialog
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    tone="danger"
    title={`Disconnect ${systemDisplayName}`}
    subtitle={`${franchiseName} · ${fpCode.toUpperCase()}`}
    pending={loading}
    confirmLabel={`Disconnect ${systemDisplayName}`}
    acknowledgement={`I understand this will permanently disconnect ${systemDisplayName}`}
    body={
      <DisconnectBody
        systemDisplayName={systemDisplayName}
        tenantName={tenantName}
      />
    }
  />
)

interface DisconnectBodyProps {
  systemDisplayName: string
  tenantName?: string
}

const DisconnectBody: React.FC<DisconnectBodyProps> = ({
  systemDisplayName,
  tenantName,
}) => (
  <Box>
    <Typography variant="body2">
      This permanently disconnects {systemDisplayName} and removes all stored
      connection data.
    </Typography>

    {tenantName && (
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Current {systemDisplayName} organisation
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {tenantName}
        </Typography>
      </Box>
    )}

    <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
      Once disconnected:
    </Typography>
    <Box component="ul" sx={{ pl: 2.5, m: 0, mt: 0.5 }}>
      <Typography component="li" variant="body2" sx={{ lineHeight: 1.5 }}>
        All stored tokens and connection data are removed
      </Typography>
      <Typography component="li" variant="body2" sx={{ lineHeight: 1.5 }}>
        Sync with {systemDisplayName} stops immediately
      </Typography>
      <Typography component="li" variant="body2" sx={{ lineHeight: 1.5 }}>
        Reconnection requires a fresh authorisation flow
      </Typography>
    </Box>
  </Box>
)

export default DisconnectDialog
